import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, Mail, Truck } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api, BOOK } from "@/lib/book";

export default function Success() {
  const [params] = useSearchParams();
  // Supports both legacy Checkout Sessions (session_id) and new PaymentIntent flow
  const sessionId = params.get("session_id");
  const paymentIntentId = params.get("payment_intent");
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId && !paymentIntentId) { setStatus("error"); return; }
    let cancelled = false;
    const url = paymentIntentId
      ? `/checkout/payment-intent/${paymentIntentId}`
      : `/checkout/session/${sessionId}`;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data: d } = await api.get(url);
        if (cancelled) return;
        setData(d);
        if (d.payment_status === "paid") { setStatus("paid"); return; }
        if (d.status === "expired" || d.payment_status === "canceled") { setStatus("expired"); return; }
        if (attempts.current >= 12) { setStatus("pending"); return; }
        setStatus("pending");
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, paymentIntentId]);

  return (
    <div className="min-h-screen">
      <Nav />
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-20 lg:py-28" data-testid="success-section">
        {status === "paid" ? (
          <div data-testid="success-paid">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">Payment confirmed</div>
            <div className="flex items-start gap-5">
              <CheckCircle2 className="w-12 h-12 text-[#D92D20] shrink-0 mt-1" />
              <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl leading-[0.92]">
                Thank you.<br />Your book is on its way.
              </h1>
            </div>
            <p className="mt-8 max-w-xl text-lg text-[#525252] leading-relaxed">
              A confirmation email is being sent to <span className="text-[#0A0A0A] font-medium">{data?.customer_email || "your inbox"}</span>. You'll get a tracking number once your order ships.
            </p>

            <div className="mt-12 grid grid-cols-12 gap-6">
              <div className="col-span-12 sm:col-span-7 bg-white border border-black/10 p-7">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] mb-4">Order summary</div>
                <div className="flex gap-5">
                  <img src={BOOK.cover} alt="" className="w-20 h-auto" />
                  <div>
                    <div className="font-display font-bold text-xl tracking-tight">{BOOK.title}</div>
                    <div className="text-xs text-[#525252]">Quantity: {data?.quantity}</div>
                  </div>
                </div>
                <dl className="mt-6 space-y-2 text-sm border-t border-black/10 pt-5">
                  <div className="flex justify-between"><dt className="text-[#525252]">Subtotal</dt><dd className="font-mono-plex">${data?.book_total?.toFixed(2)}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#525252]">Shipping</dt><dd className="font-mono-plex">${data?.shipping_cost?.toFixed(2)}</dd></div>
                  <div className="flex justify-between text-base pt-3 border-t border-black/10 mt-3">
                    <dt className="font-medium">Total paid</dt>
                    <dd className="font-mono-plex font-bold">${data?.amount_total?.toFixed(2)}</dd>
                  </div>
                </dl>
              </div>
              <div className="col-span-12 sm:col-span-5 bg-[#0A0A0A] text-[#FFFFFF] p-7 space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D92D20]">What's next</div>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <Mail className="w-5 h-5 text-[#D92D20] mt-0.5 shrink-0" />
                  <div>Order confirmation email within minutes.</div>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <Truck className="w-5 h-5 text-[#D92D20] mt-0.5 shrink-0" />
                  <div>Tracking number once shipped. Worldwide delivery.</div>
                </div>
              </div>
            </div>

            <Link to="/" className="mt-12 inline-flex items-center gap-3 border border-[#0A0A0A] px-7 py-4 hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="back-home-btn">
              Back to the book <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : status === "expired" ? (
          <div data-testid="success-expired">
            <h1 className="font-display font-black tracking-tighter text-5xl">Payment session expired.</h1>
            <p className="mt-6 text-lg text-[#525252]">Please start a new checkout.</p>
            <Link to="/checkout" className="mt-8 inline-flex items-center gap-3 bg-[#D92D20] text-white px-7 py-4">Try again <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : status === "error" ? (
          <div data-testid="success-error">
            <h1 className="font-display font-black tracking-tighter text-5xl">Something went wrong.</h1>
            <p className="mt-6 text-lg text-[#525252]">We couldn't verify your payment. Please contact support.</p>
            <Link to="/" className="mt-8 inline-flex items-center gap-3 border border-[#0A0A0A] px-7 py-4">Back home <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div data-testid="success-pending">
            <div className="flex items-center gap-3 text-[#525252]">
              <Loader2 className="w-5 h-5 animate-spin" /> Verifying payment...
            </div>
            <h1 className="mt-6 font-display font-black tracking-tighter text-5xl">Hang tight.</h1>
            <p className="mt-4 text-lg text-[#525252]">We're confirming your payment with Stripe. This usually takes a few seconds.</p>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
